// src/pages/ReportesGanancia.js
// Requiere instalar charts:
// npm i chart.js react-chartjs-2
import React, {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {Link} from 'react-router-dom'

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import {Bar, Line} from 'react-chartjs-2'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend)

const API_BASE = 'https://sistemarab.com/CasinoApp'

function ymNow() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
function formatCLP(n) {
  const v = Number(n || 0)
  return v.toLocaleString('es-CL', {style: 'currency', currency: 'CLP', maximumFractionDigits: 0})
}

function safeNum(x) {
  const n = Number(x || 0)
  return Number.isFinite(n) ? n : 0
}

export default function ReportesGanancia() {
  const [month, setMonth] = useState(ymNow())
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [items, setItems] = useState([]) // rows de getMenuMes (con márgenes por opción)

  async function load() {
    setErr('')
    setLoading(true)
    try {
      const res = await axios.get(`${API_BASE}/getMenuMes.php`, {params: {ym: month}})
      setItems(res.data?.items || [])
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Error cargando reporte')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  const daily = useMemo(() => {
    // margen potencial del día: suma de márgenes de las opciones publicadas
    const rows = items.map((r) => {
      const margen =
        safeNum(r.fondo1_margen) +
        safeNum(r.fondo2_margen) +
        safeNum(r.hipocalorico_margen) +
        safeNum(r.vegetariano_margen) +
        safeNum(r.otro_margen)

      const costo =
        safeNum(r.fondo1_costo) +
        safeNum(r.fondo2_costo) +
        safeNum(r.hipocalorico_costo) +
        safeNum(r.vegetariano_costo) +
        safeNum(r.otro_costo)

      const precio =
        safeNum(r.fondo1_precio) +
        safeNum(r.fondo2_precio) +
        safeNum(r.hipocalorico_precio) +
        safeNum(r.vegetariano_precio) +
        safeNum(r.otro_precio)

      return {fecha: r.fecha, margen, costo, precio, raw: r}
    })

    // ordenar por fecha
    rows.sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0))
    return rows
  }, [items])

  const monthTotals = useMemo(() => {
    return daily.reduce(
      (acc, d) => {
        acc.margen += safeNum(d.margen)
        acc.costo += safeNum(d.costo)
        acc.precio += safeNum(d.precio)
        return acc
      },
      {margen: 0, costo: 0, precio: 0}
    )
  }, [daily])

  const barData = useMemo(() => {
    const labels = daily.map((d) => d.fecha.slice(8, 10)) // día "01..31"
    const data = daily.map((d) => safeNum(d.margen))
    return {
      labels,
      datasets: [{label: 'Margen potencial por día', data}],
    }
  }, [daily])

  const lineData = useMemo(() => {
    let acc = 0
    const labels = daily.map((d) => d.fecha.slice(8, 10))
    const data = daily.map((d) => {
      acc += safeNum(d.margen)
      return acc
    })
    return {
      labels,
      datasets: [{label: 'Margen acumulado del mes', data}],
    }
  }, [daily])

  return (
    <section className="section">
      <div className="container">
        <div className="level">
          <div className="level-left">
            <div>
              <h1 className="title is-4 mb-1">📈 Reportes de Ganancia</h1>
              <p className="subtitle is-6">
                Reporte basado en el <b>menú publicado</b> (margen potencial). Para margen real hay que guardar la elección por RUT.
              </p>
            </div>
          </div>

          <div className="level-right" style={{gap: 10}}>
            <input className="input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            <button className={`button is-primary ${loading ? 'is-loading' : ''}`} onClick={load}>
              Recargar
            </button>
          </div>
        </div>

        {err && <div className="notification is-danger is-light">{err}</div>}

        <div className="columns">
          <div className="column">
            <div className="box">
              <p className="has-text-grey">Costo total (publicado)</p>
              <p className="title is-4">{formatCLP(monthTotals.costo)}</p>
            </div>
          </div>
          <div className="column">
            <div className="box">
              <p className="has-text-grey">Precio total (publicado)</p>
              <p className="title is-4">{formatCLP(monthTotals.precio)}</p>
            </div>
          </div>
          <div className="column">
            <div className="box">
              <p className="has-text-grey">Margen potencial mes</p>
              <p className="title is-4">{formatCLP(monthTotals.margen)}</p>
            </div>
          </div>
        </div>

        <div className="columns is-multiline">
          <div className="column is-12">
            <div className="box">
              <p className="has-text-weight-semibold mb-2">Margen por día</p>
              <Bar data={barData} options={{responsive: true, plugins: {legend: {display: true}}}} />
            </div>
          </div>

          <div className="column is-12">
            <div className="box">
              <p className="has-text-weight-semibold mb-2">Margen acumulado</p>
              <Line data={lineData} options={{responsive: true, plugins: {legend: {display: true}}}} />
            </div>
          </div>

          <div className="column is-12">
            <div className="box">
              <p className="has-text-weight-semibold mb-3">Detalle por día</p>

              {loading ? (
                <div>⏳ Cargando...</div>
              ) : (
                <div style={{overflowX: 'auto'}}>
                  <table className="table is-striped is-fullwidth">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th className="has-text-right">Costo</th>
                        <th className="has-text-right">Precio</th>
                        <th className="has-text-right">Margen</th>
                        <th>Nota</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daily.map((d) => (
                        <tr key={d.fecha}>
                          <td>{d.fecha}</td>
                          <td className="has-text-right">{formatCLP(d.costo)}</td>
                          <td className="has-text-right">{formatCLP(d.precio)}</td>
                          <td className="has-text-right">
                            <span className={`tag ${d.margen >= 0 ? 'is-success' : 'is-danger'}`}>
                              {formatCLP(d.margen)}
                            </span>
                          </td>
                          <td>{d.raw?.nota || ''}</td>
                        </tr>
                      ))}
                      {daily.length === 0 && (
                        <tr>
                          <td colSpan={5} className="has-text-grey">
                            No hay datos para el mes seleccionado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="help mt-2">
                Nota: este reporte usa los costos/precios de los platos asignados al menú. Si un día no tiene menú, no aparece.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
